/* src/pages/PublicForm.jsx
 * Public form renderer - displays form and handles submissions
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import FormRenderer from '../components/FormRenderer';
import { LAYOUT_OPTIONS } from '../utils/formTemplates';
import { injectAllPixels, fireAllConversionEvents } from '../utils/tracking';
import { collectAttribution } from '../utils/attribution';
import { parseCssColor, isUsableSurface, pickReadableText } from '../utils/embedTheme';
import { resolveFromName } from '../utils/replyIdentity';
import { resolveConsentBusinessName } from '../utils/smsConsent';

import { GET_FORM_FOR_EMBED_URL } from '../utils/api';

// Cloud Run URL for form submissions (separate from Cloud Functions base)
const FUNCTION_URL = process.env.REACT_APP_FUNCTION_URL ||
  'https://handlesubmission-5sg7uqqiaq-uc.a.run.app';
const GET_FORM_URL = GET_FORM_FOR_EMBED_URL;

const SUBMIT_PROGRESS_STEPS = [
  { label: 'Sending your details' },
  { label: 'Verifying', hint: 'Checking everything looks good' },
  { label: 'Almost done' },
  { label: 'Received' },
];

export default function PublicForm() {
  const { formId } = useParams();
  // Stable for the lifetime of this page — location.search never changes here.
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === 'true';

  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [layout, setLayout] = useState('modern');
  const [layoutConfig, setLayoutConfig] = useState(null);
  const [theme, setTheme] = useState({
    primaryColor: '#1e40af',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    borderColor: '#dddddd',
    borderRadius: '8px',
    buttonColor: '#1e40af',
    buttonTextColor: '#ffffff',
  });
  const postHeightRef = useRef(null);
  const loadingRef = useRef(true);
  // Stores attribution params forwarded by embed.js via postMessage (adopt path).
  // Read at submit time so a message that arrives just after ready is included.
  const embedAttributionRef = useRef(null);
  const parentOriginRef = useRef('*');
  const emitLeadDataRef = useRef(false);
  const [hostContext, setHostContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loaderActive, setLoaderActive] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const loaderStartRef = useRef(0);
  const progressTimersRef = useRef([]);
  const submittedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    loadForm();
    // Capture click IDs / UTMs immediately (persists first touch in localStorage;
    // referrer carries the parent page URL for iframe embeds)
    collectAttribution();
  }, [formId]);

  // Keep loadingRef in sync so the postHeight closure can read the live value
  // without being stale-captured (postHeight is defined once on mount).
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Inject tracking pixels when form loads
  useEffect(() => {
    if (form?.trackingPixels && form.trackingPixels.length > 0) {
      injectAllPixels(form.trackingPixels);
    }
  }, [form]);

  // Auto-fit height: measure true content height and post to the parent frame.
  // embed.js listens for smartforms:resize and sets iframe.style.height exactly,
  // eliminating the inner scrollbar.  ResizeObserver on documentElement catches
  // all DOM changes (form load, validation errors, success state, window resize).
  //
  // IMPORTANT: we use setTimeout (not requestAnimationFrame) as the debounce
  // primitive.  rAF is paused in hidden/background tabs — so a form opened via
  // cmd/ctrl-click or loaded in a background iframe would never post its resize
  // message and stay stuck at the 600 px fallback.  setTimeout fires in hidden
  // tabs (browsers may throttle it to ~1 s, but it always fires eventually).
  // visibilitychange + pageshow then re-post the moment the tab comes to the
  // foreground, self-healing any measurement that landed while hidden.
  useEffect(() => {
    if (!isEmbed) return;

    window.parent.postMessage({ type: 'smartforms:ready', formId }, '*');

    let lastHeight = null;
    let timerId = null;

    function postHeight() {
      if (loadingRef.current) return; // hold the iframe at its initial height while the form is loading
      if (timerId !== null) clearTimeout(timerId);
      timerId = setTimeout(() => {
        timerId = null;
        const h = document.body.scrollHeight;
        // Guard: only post when height actually changed by more than 1 px so
        // we don't trigger an infinite resize loop.
        if (lastHeight === null || Math.abs(h - lastHeight) > 1) {
          lastHeight = h;
          window.parent.postMessage({ type: 'smartforms:resize', formId, height: h }, '*');
        }
      }, 0);
    }
    // Expose postHeight so external effects (e.g. hostContext change) can
    // trigger a re-measure without re-registering all the listeners.
    postHeightRef.current = postHeight;

    // Re-post when the tab surfaces (hidden-tab load self-heals on focus).
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') postHeight();
    }

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(postHeight);
      ro.observe(document.documentElement);
    }

    window.addEventListener('resize', postHeight);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handleVisibilityChange); // bfcache restore

    // Initial measurement — small delay lets React finish first paint.
    const initialTimer = setTimeout(postHeight, 150);

    return () => {
      postHeightRef.current = null;
      if (ro) ro.disconnect();
      window.removeEventListener('resize', postHeight);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handleVisibilityChange);
      clearTimeout(initialTimer);
      if (timerId !== null) clearTimeout(timerId);
    };
  }, [formId, isEmbed]);

  // Host context: receive from embed.js so the form can match the host page.
  // Seeds from URL params first (create-path, no FOUC); then listens for the
  // postMessage reply that embed.js sends synchronously on smartforms:ready.
  useEffect(() => {
    if (!isEmbed) return;
    const params = new URLSearchParams(window.location.search);
    const seedBg     = params.get('hostBg');
    const seedFont   = params.get('hostFont');
    const seedWidth  = params.get('hostWidth');
    const seedAccent = params.get('hostAccent');
    if (params.get('emitLeadData') === 'true') emitLeadDataRef.current = true;
    if (seedBg || seedFont || seedWidth || seedAccent) {
      setHostContext({
        bg:     seedBg     || undefined,
        font:   seedFont   || undefined,
        width:  seedWidth  ? (parseInt(seedWidth, 10) || undefined) : undefined,
        accent: seedAccent || undefined,
      });
    }
    function handleHostContext(e) {
      try {
        const d = e.data;
        if (!d || typeof d !== 'object') return;
        if (d.formId !== formId) return;

        if (d.type === 'smartforms:attribution') {
          // Forwarded from embed.js — parent landing-page URL is the source of truth
          // for ad params on adopted iframes.  Store and immediately persist so
          // first-touch is recorded before the visitor might navigate away.
          if (d.params && typeof d.params === 'object') {
            embedAttributionRef.current = d.params;
            collectAttribution(d.params);
          }
          return;
        }

        if (d.type !== 'smartforms:host-context') return;
        if (d.parentOrigin) parentOriginRef.current = d.parentOrigin;
        if (d.emitLeadData) emitLeadDataRef.current = true;
        setHostContext({
          bg:     d.bg     || undefined,
          font:   d.font   || undefined,
          width:  d.width  ? (Number(d.width) || undefined) : undefined,
          accent: d.accent || undefined,
        });
      } catch (_) {}
    }
    window.addEventListener('message', handleHostContext);
    return () => window.removeEventListener('message', handleHostContext);
  }, [formId, isEmbed]);

  // Re-measure and post height after host-context applies — font/theme changes
  // affect rendered height.  ResizeObserver catches most cases; this is the
  // explicit belt-and-suspenders call the spec requires.
  useEffect(() => {
    if (!isEmbed || !hostContext) return;
    const timer = setTimeout(() => {
      if (postHeightRef.current) postHeightRef.current();
    }, 0);
    return () => clearTimeout(timer);
  }, [hostContext, isEmbed]);

  // Fire one measurement the moment loading finishes — this is what makes the
  // iframe jump from the placeholder height to the real content height promptly.
  // Kept separate from the hostContext effect because that one early-returns when
  // hostContext is null (create-path / no-host-context embeds would be missed).
  useEffect(() => {
    if (!isEmbed || loading) return;
    const timer = setTimeout(() => {
      if (postHeightRef.current) postHeightRef.current();
    }, 0);
    return () => clearTimeout(timer);
  }, [loading, isEmbed]);

  function extractSubmitterEmail(fields, data) {
    if (!fields || !data) return null;
    const emailField = fields.find((f) => f.type === 'email' || f.name === 'email');
    if (!emailField) return null;
    const raw = data[emailField.name];
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    return trimmed || null;
  }

  function emitEmbedSubmitSuccess(result) {
    if (!isEmbed || result.spamStatus === 'spam') return;
    const leadId = result.conversationId || result.submissionId || null;
    const payload = {
      type: 'gocta:submit-success',
      formId,
      leadId,
      submissionId: result.submissionId || null,
    };
    if (emitLeadDataRef.current) {
      const email = extractSubmitterEmail(form?.fields, formData);
      if (email) payload.email = email;
    }
    try {
      window.parent.postMessage(payload, parentOriginRef.current || '*');
    } catch (_) {}
  }

  async function loadForm() {
    try {
      const res = await fetch(`${GET_FORM_URL}?formId=${encodeURIComponent(formId)}`);
      if (res.ok) {
        const formData = await res.json();
        setForm(formData);
        if (formData.theme) {
          setTheme(formData.theme);
        }
        const formLayout = formData.layout || 'modern';
        setLayout(formLayout);
        const layoutOption = LAYOUT_OPTIONS.find(l => l.id === formLayout) || LAYOUT_OPTIONS[0];
        setLayoutConfig(layoutOption);
      } else if (res.status === 404) {
        setError('Form not found');
      } else {
        setError('Failed to load form');
      }
    } catch (error) {
      console.error('Error loading form:', error);
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Fire browser-side conversion events and get event IDs for deduplication
      let eventIds = {};
      if (form?.trackingPixels && form.trackingPixels.length > 0) {
        eventIds = fireAllConversionEvents(form.trackingPixels, formData, { formId, formName: form?.name || form?.title }) || {};
        console.log('[TRACKING] Browser events fired, event IDs:', eventIds);
      }

      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId: formId,
          data: formData,
          trackingData: {
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            eventIds: eventIds, // Send event IDs for server-side deduplication
            // Merge any attribution forwarded by embed.js (adopt-path parent params).
            // embedAttributionRef.current is null for direct/non-embed loads → no-op.
            attribution: collectAttribution(embedAttributionRef.current),
          },
        }),
      });

      const result = await response.json();

      if (result.redirect) {
        // Spam detected - redirect
        window.location.href = result.url;
        return;
      }

      if (response.ok && result.success) {
        // Events already fired before submission (for better reliability)
        emitEmbedSubmitSuccess(result);
        setSubmitted(true);
        setFormData({});
      } else {
        setError(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleFieldChange(fieldName, value) {
    setFormData({
      ...formData,
      [fieldName]: value,
    });
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    submittedRef.current = submitted;
  }, [submitted]);

  function clearProgressTimers() {
    progressTimersRef.current.forEach(clearTimeout);
    progressTimersRef.current = [];
  }

  function scheduleProgressTimer(fn, delay) {
    const id = setTimeout(() => {
      progressTimersRef.current = progressTimersRef.current.filter((t) => t !== id);
      if (mountedRef.current) fn();
    }, delay);
    progressTimersRef.current.push(id);
    return id;
  }

  // Time-driven submit progress: steps 1-3 advance on timers; step 4 only on real success.
  useEffect(() => {
    if (submitting) {
      clearProgressTimers();
      loaderStartRef.current = Date.now();
      setLoaderActive(true);
      setProgressStep(0);
      scheduleProgressTimer(() => {
        if (!submittedRef.current) setProgressStep(1);
      }, 700);
      scheduleProgressTimer(() => {
        if (!submittedRef.current) setProgressStep(2);
      }, 1800);
    }
  }, [submitting]);

  useEffect(() => {
    if (!submitted || !loaderActive) return;
    clearProgressTimers();
    setProgressStep(3);
    const elapsed = Date.now() - loaderStartRef.current;
    const remainingMin = Math.max(0, 600 - elapsed);
    scheduleProgressTimer(() => {
      setLoaderActive(false);
    }, remainingMin + 500);
    return clearProgressTimers;
  }, [submitted, loaderActive]);

  useEffect(() => {
    if (!submitting && !submitted && loaderActive) {
      clearProgressTimers();
      setLoaderActive(false);
      setProgressStep(0);
    }
  }, [submitting, submitted, loaderActive]);

  useEffect(() => {
    return clearProgressTimers;
  }, []);

  useEffect(() => {
    if (!isEmbed) return;
    const timer = setTimeout(() => {
      if (postHeightRef.current) postHeightRef.current();
    }, 0);
    return () => clearTimeout(timer);
  }, [loaderActive, isEmbed]);

  // Effective theme: hardcoded defaults ← host context (fills gaps) ← form.theme (brand always wins).
  //
  // fontFamily is intentionally absent from Firestore-stored themes today (not
  // exposed in the form builder), so host font applies automatically for all
  // existing forms without any per-form configuration.
  //
  // Background adoption: only applied when the host bg passes the readability
  // guard (lum ≤ 0.92 AND sat ≥ 0.15 — not near-white, not unsaturated gray/
  // black).  When the guard rejects, the default white card is kept.
  // Hard rule: Phase B can never make the form less readable than Phase A.
  const effectiveTheme = useMemo(() => {
    const base = { ...theme, fontFamily: '' };
    if (!isEmbed || !hostContext) return base;
    const derived = { ...base };

    // Font: host font fills the new fontFamily field.
    if (hostContext.font && !form?.theme?.fontFamily) {
      derived.fontFamily = hostContext.font;
    }

    // Background/text: adopt host bg only when it passes the readability guard
    // and the form has no explicit brand background.
    if (!form?.theme?.backgroundColor && hostContext.bg) {
      const rgb = parseCssColor(hostContext.bg);
      if (rgb && isUsableSurface(rgb)) {
        derived.backgroundColor = hostContext.bg;
        if (!form?.theme?.textColor) {
          derived.textColor = pickReadableText(rgb);
        }
      }
    }

    // Accent: adopt host brand color for the submit button only when it passes
    // the readability guard AND the form has no explicit brand primaryColor.
    if (!form?.theme?.primaryColor && hostContext.accent) {
      const argb = parseCssColor(hostContext.accent);
      if (argb && isUsableSurface(argb)) {
        derived.buttonColor = hostContext.accent;
        derived.buttonTextColor = pickReadableText(argb);
      }
    }

    return derived;
  }, [theme, hostContext, isEmbed, form]);

  // In embed mode, suppress the height-inflating min-height so ResizeObserver
  // measures the true content height for all states.
  const embedBaseStyle = isEmbed
    ? { height: 'auto', minHeight: 'auto', padding: '2rem', boxSizing: 'border-box' }
    : { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  if (loading) {
    return (
      <>
        {isEmbed && <style>{`html,body{height:auto!important;min-height:auto!important;}`}</style>}
        <div style={embedBaseStyle}>
          <div>Loading form...</div>
        </div>
      </>
    );
  }

  if (error && !form) {
    return (
      <>
        {isEmbed && <style>{`html,body{height:auto!important;min-height:auto!important;}`}</style>}
        <div style={embedBaseStyle}>
          <div style={{ color: '#dc3545' }}>{error}</div>
        </div>
      </>
    );
  }

  if (submitted && !loaderActive) {
    return (
      <>
        {isEmbed && <style>{`html,body{height:auto!important;min-height:auto!important;}`}</style>}
        <div style={isEmbed
          ? { padding: '2rem', background: 'transparent', boxSizing: 'border-box',
              fontFamily: effectiveTheme.fontFamily || undefined }
          : { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }
        }>
          <div style={{
            background: effectiveTheme.backgroundColor,
            color: effectiveTheme.textColor,
            padding: '3rem',
            borderRadius: effectiveTheme.borderRadius,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            textAlign: 'center',
            maxWidth: '500px',
            ...(isEmbed ? { margin: '0 auto' } : {}),
          }}>
            <h2 style={{ color: '#28a745', marginBottom: '1rem' }}>✓ Thank You!</h2>
            <p>Your submission has been received successfully.</p>
          </div>
        </div>
      </>
    );
  }

  // Get embed settings from URL params or use defaults
  // (isEmbed is already declared at the top of the component)
  const urlParams = new URLSearchParams(window.location.search);
  const embedSettings = isEmbed ? {
    width: urlParams.get('width') || '100%',
    maxWidth: urlParams.get('maxWidth') || '600px',
    padding: urlParams.get('padding') || '2rem',
    borderRadius: urlParams.get('borderRadius') || effectiveTheme.borderRadius,
    responsive: urlParams.get('responsive') !== 'false',
    noScroll: urlParams.get('noScroll') === 'true',
  } : null;

  // No scroll: embed URL overrides; otherwise use form designSettings.showScroller (false = no scroll)
  const noScroll = isEmbed
    ? (embedSettings?.noScroll ?? false)
    : (form?.designSettings?.showScroller === false);

  const containerStyle = embedSettings ? {
    width: embedSettings.width,
    maxWidth: embedSettings.maxWidth,
    margin: '0 auto',
    background: effectiveTheme.backgroundColor,
    padding: embedSettings.padding,
    borderRadius: embedSettings.borderRadius,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    ...(embedSettings.noScroll ? { overflow: 'hidden' } : {}),
  } : {
    maxWidth: '600px',
    width: '100%',
    margin: '0 auto',
    background: effectiveTheme.backgroundColor,
    padding: '2rem',
    borderRadius: effectiveTheme.borderRadius,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    boxSizing: 'border-box',
    ...(noScroll ? { overflow: 'hidden' } : {}),
  };

  return (
    <>
      <style>{`
        ${isEmbed ? `
          html, body { height: auto !important; min-height: auto !important; }
        ` : ''}
        ${noScroll ? `
          html, body { overflow: hidden !important; height: auto !important; min-height: auto !important; }
        ` : ''}
        @media (max-width: 768px) {
          .smartform-container {
            padding: 1rem !important;
            max-width: 100% !important;
          }
          .smartform-field {
            font-size: 16px !important; /* Prevents zoom on iOS */
          }
        }
        @media (max-width: 480px) {
          .smartform-container {
            padding: 0.75rem !important;
          }
        }
        @keyframes submitProgressPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.12); }
        }
        @keyframes submitProgressBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(250%); }
        }
        @keyframes submitProgressSpin {
          to { transform: rotate(360deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .submit-progress-pulse,
          .submit-progress-bar-fill,
          .submit-progress-spin {
            animation: none !important;
          }
        }
      `}</style>
      <div style={{ 
        minHeight: (noScroll || isEmbed) ? 'auto' : '100vh',
        background: isEmbed ? 'transparent' : '#f5f5f5',
        padding: embedSettings ? '1rem' : '2rem',
        boxSizing: 'border-box',
        fontFamily: effectiveTheme.fontFamily || undefined,
        ...(noScroll ? { overflow: 'hidden' } : {}),
      }}>
        <div style={containerStyle} className="smartform-container">
          <h1 style={{ 
            marginTop: 0, 
            marginBottom: '2rem', 
            color: effectiveTheme.textColor,
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
          }}>{form?.name || 'Form'}</h1>

        {error && (
          <div style={{
            background: '#fee',
            color: '#c33',
            padding: '0.75rem',
            borderRadius: '4px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {loaderActive ? (
          <div
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={SUBMIT_PROGRESS_STEPS[progressStep]?.label}
            style={{
              padding: '1.5rem 0.5rem',
              minHeight: '220px',
              fontFamily: effectiveTheme.fontFamily || undefined,
            }}
          >
            <ul style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
            }}>
              {SUBMIT_PROGRESS_STEPS.map((step, idx) => {
                const accent = effectiveTheme.buttonColor || effectiveTheme.primaryColor;
                const isDone = idx < progressStep;
                const isActive = idx === progressStep;
                const isPending = idx > progressStep;
                const labelOpacity = isPending ? 0.45 : 1;
                return (
                  <li
                    key={step.label}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.875rem',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        flexShrink: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        marginTop: '0.1rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isDone ? (
                        <span style={{
                          width: '1.5rem',
                          height: '1.5rem',
                          borderRadius: '50%',
                          background: accent,
                          color: effectiveTheme.buttonTextColor || '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                        }}>✓</span>
                      ) : isActive ? (
                        <span
                          className="submit-progress-pulse"
                          style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '50%',
                            border: `2px solid ${accent}`,
                            boxShadow: `0 0 0 3px ${accent}22`,
                            animation: 'submitProgressPulse 1.4s ease-in-out infinite',
                          }}
                        />
                      ) : (
                        <span style={{
                          width: '1.5rem',
                          height: '1.5rem',
                          borderRadius: '50%',
                          border: `2px solid ${effectiveTheme.borderColor || '#dddddd'}`,
                          opacity: 0.5,
                        }} />
                      )}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: 'block',
                        color: effectiveTheme.textColor,
                        opacity: labelOpacity,
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '1rem',
                        lineHeight: 1.4,
                      }}>
                        {step.label}
                      </span>
                      {step.hint && isActive && (
                        <span style={{
                          display: 'block',
                          marginTop: '0.2rem',
                          color: effectiveTheme.textColor,
                          opacity: 0.65,
                          fontSize: '0.875rem',
                          lineHeight: 1.4,
                        }}>
                          {step.hint}
                        </span>
                      )}
                    </span>
                    {isActive && idx === 2 && (
                      <span
                        aria-hidden="true"
                        className="submit-progress-spin"
                        style={{
                          flexShrink: 0,
                          width: '1.125rem',
                          height: '1.125rem',
                          marginTop: '0.15rem',
                          border: `2px solid ${effectiveTheme.borderColor || '#dddddd'}`,
                          borderTopColor: accent,
                          borderRadius: '50%',
                          animation: 'submitProgressSpin 0.9s linear infinite',
                        }}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
            <div
              aria-hidden="true"
              style={{
                marginTop: '1.75rem',
                height: '4px',
                borderRadius: '2px',
                background: `${effectiveTheme.borderColor || '#dddddd'}55`,
                overflow: 'hidden',
              }}
            >
              <div
                className="submit-progress-bar-fill"
                style={{
                  height: '100%',
                  width: '40%',
                  borderRadius: '2px',
                  background: effectiveTheme.buttonColor || effectiveTheme.primaryColor,
                  animation: 'submitProgressBar 1.6s ease-in-out infinite',
                }}
              />
            </div>
          </div>
        ) : form?.fields && form.fields.length > 0 ? (
          <FormRenderer
            fields={form.fields}
            rows={form.rows || []}
            theme={effectiveTheme}
            layout={layout}
            layoutConfig={layoutConfig}
            fieldDirection={form.designSettings?.fieldDirection || 'horizontal'}
            formName={form.name || 'Form'}
            formData={formData}
            onFieldChange={handleFieldChange}
            onSubmit={handleSubmit}
            readOnly={submitting}
            submitting={submitting}
            smsOptIn={form.settings?.smsOptIn}
            consentBusinessName={resolveConsentBusinessName({
              businessName: resolveFromName({ formBusinessName: form.settings?.businessName }),
              formName: form.name,
            })}
          />
        ) : (
          <p style={{ color: effectiveTheme.textColor }}>No fields configured for this form.</p>
        )}
        </div>
      </div>
    </>
  );
}
